<template>
    <PageWrapper>
        <template #headline>
            <fa icon="fa-solid fa-file-signature" />&nbsp;
            {{
                route.name === 'payment_account.create'
                    ? $t('create')
                    : $t('edit')
            }}&nbsp; {{ $t('payment_account') }}&nbsp;
            {{ paymentAccount.id }}
        </template>

        <template #actions>
            <BackButton
                v-if="$router.options.history.state.back"
                @click="router.go(-1)"
            />
            <BackButton
                v-if="
                    paymentAccount.id &&
                    $router.options.history.state.back
                        ?.toString()
                        .includes('/payment_accounts') &&
                    $router.options.history.state.back !==
                        `/payment-accounts/${paymentAccount.id}`
                "
                icon="fa-eye"
                title="payment_account"
                @click="goToDetails"
            />
            <BackButton
                v-if="
                    $router.options.history.state.back !== '/payment-accounts'
                "
                icon="fa-list"
                title="payment_accounts"
                @click="toPaymentAccounts"
            />

            <SaveButton @click="savePaymentAccount" />
        </template>

        <AccordionBasic
            title="payment_account_details"
            class="mb-2"
            :open="true"
        >
            <TextField
                v-model="paymentAccount.logo_url"
                label="logo_url"
                name="logo_url"
                help="help_logo_url"
            />
            <SelectField
                v-model="paymentAccount.type"
                :options="mapEnumToArray(PaymentAccountType)"
                name="type"
                label="type"
                help="help_type"
                :on-search="loadPaymentAccountTypeSelectOptions"
                :required="true"
            />
            <TextField
                v-model="paymentAccount.name"
                label="name"
                name="name"
                help="help_name"
                :required="true"
            />
            <TextField
                v-model="paymentAccount.description"
                label="description"
                name="description"
                help="help_description"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.webhook_api_key"
                label="webhook_api_key"
                name="webhook_api_key"
                help="help_webhook_api_key"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.payment_reference_prefix"
                label="payment_reference_prefix"
                name="payment_reference_prefix"
                help="help_payment_reference_prefix"
                :required="true"
            />

            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.payment_endpoint"
                label="payment_endpoint"
                name="payment_endpoint"
                help="help_payment_endpoint"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.auth_endpoint"
                label="auth_endpoint"
                name="auth_endpoint"
                help="help_auth_endpoint"
                :required="true"
            />
            <TextField
                v-model="paymentAccount.api_endpoint"
                label="api_endpoint"
                name="api_endpoint"
                help="help_api_endpoint"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.api_token"
                label="api_token"
                name="api_token"
                help="help_api_token"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.SECUPAY"
                v-model="paymentAccount.client_id"
                label="client_id"
                name="client_id"
                help="help_client_id"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.SECUPAY"
                v-model="paymentAccount.client_secret"
                label="client_secret"
                name="client_secret"
                help="help_client_secret"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.SECUPAY"
                v-model="paymentAccount.master_contract_id"
                name="master_contract_id"
                label="master_contract_id"
                help="help_master_contract_id"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.SECUPAY"
                v-model="paymentAccount.checkout_template_id"
                name="checkout_template_id"
                label="checkout_template_id"
                help="help_checkout_template_id"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.SECUPAY"
                v-model="paymentAccount.stakeholder_contract_id"
                name="stakeholder_contract_id"
                label="stakeholder_contract_id"
                help="help_stakeholder_contract_id"
                :required="paymentAccount.enable_stakeholder_payment"
            />
            <ToggleSwitch
                v-if="paymentAccount.type === PaymentAccountType.SECUPAY"
                v-model="paymentAccount.enable_stakeholder_payment"
                name="enable_stakeholder_payment"
                label="enable_stakeholder_payment"
                help="help_enable_stakeholder_payment"
            />
            <NumberField
                v-if="
                    paymentAccount.type === PaymentAccountType.SECUPAY &&
                    paymentAccount.enable_stakeholder_payment
                "
                v-model="paymentAccount.stakeholder_fee"
                name="stakeholder_fee"
                label="stakeholder_fee"
                help="help_stakeholder_fee"
                :min="0"
                :max="1"
                :required="true"
            />
            <ToggleSwitch
                v-model="paymentAccount.sync_enabled"
                name="sync_enabled"
                label="sync_enabled"
                help="help_sync_enabled"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.currency"
                label="currency"
                name="currency"
                help="help_currency"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.deposit_account_holder"
                label="deposit_account_holder"
                name="deposit_account_holder"
                help="help_deposit_account_holder"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.deposit_iban"
                label="deposit_iban"
                name="deposit_iban"
                help="help_deposit_iban"
                :required="true"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.deposit_bic"
                label="deposit_bic"
                name="deposit_bic"
                help="help_deposit_bic"
                :required="true"
            />

            <NumberField
                v-model="paymentAccount.limit_credit_card_order"
                name="limit_credit_card_order"
                label="limit_credit_card_order"
                help="help_limit_credit_card_order"
                :min="0"
                :required="true"
            />
            <NumberField
                v-if="
                    paymentAccount.type === PaymentAccountType.LEMONWAY &&
                    paymentAccount.can_deposit
                "
                v-model="paymentAccount.limit_credit_card_deposit"
                name="limit_credit_card_deposit"
                label="limit_credit_card_deposit"
                help="help_limit_credit_card_deposit"
                :min="0"
                :required="true"
            />

            <NumberField
                v-model="paymentAccount.limit_bank_transfer_order"
                name="limit_bank_transfer_order"
                label="limit_bank_transfer_order"
                help="help_limit_bank_transfer_order"
                :min="0"
                :required="true"
            />

            <NumberField
                v-if="
                    paymentAccount.type === PaymentAccountType.LEMONWAY &&
                    paymentAccount.can_deposit
                "
                v-model="paymentAccount.limit_bank_transfer_deposit"
                name="limit_bank_transfer_deposit"
                label="limit_bank_transfer_deposit"
                help="help_limit_bank_transfer_deposit"
                :min="0"
                :required="true"
            />
            <NumberField
                v-if="
                    paymentAccount.type === PaymentAccountType.LEMONWAY &&
                    paymentAccount.can_withdraw
                "
                v-model="paymentAccount.limit_bank_transfer_withdrawal"
                name="limit_bank_transfer_withdrawal"
                label="limit_bank_transfer_withdrawal"
                help="help_limit_bank_transfer_withdrawal"
                :min="0"
                :required="true"
            />

            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.deposit_account_holder_address"
                label="deposit_account_holder_address"
                name="deposit_account_holder_address"
                help="help_deposit_account_holder_address"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.deposit_account_holder_country"
                label="deposit_account_holder_country"
                name="deposit_account_holder_country"
                help="help_deposit_account_holder_country"
            />
            <TextField
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.healthcheck_wallet"
                label="healthcheck_wallet"
                name="healthcheck_wallet"
                help="help_healthcheck_wallet"
            />

            <ToggleSwitch
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.uses_proxy"
                name="uses_proxy"
                label="uses_proxy"
                help="help_uses_proxy"
            />
            <ToggleSwitch
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.can_deposit"
                name="can_deposit"
                label="can_deposit"
                help="help_can_deposit"
            />
            <ToggleSwitch
                v-if="paymentAccount.type === PaymentAccountType.LEMONWAY"
                v-model="paymentAccount.can_withdraw"
                name="can_withdraw"
                label="can_withdraw"
                help="help_can_withdraw"
            />
        </AccordionBasic>
    </PageWrapper>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import BackButton from '@partials/actions/BackButton.vue'
import {
    AccordionBasic,
    NumberField,
    SelectField,
    TextField,
    ToggleSwitch,
} from '@src/components'
import SaveButton from '@src/partials/actions/SaveButton.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'

import { useEnum } from '@src/composables/common'
import useEditLock from '@src/composables/editLock'
import { PaymentAccountType } from '@src/enums/PaymentAccount'
import {
    PaymentAccount,
    PaymentAccountDetails,
} from '@src/types/PaymentAccount'
import {
    createPaymentAccount,
    getPaymentAccount,
    getPaymentAccounts,
    updatePaymentAccount,
} from '@src/utils/api/paymentAccounts'
import { useToastStore } from '@src/utils/stores/toast'
import { useI18n } from 'vue-i18n'
const { mapEnumToArray } = useEnum()

const router = useRouter()
const route = useRoute()
const toast = useToastStore()
const i18n = useI18n()

useEditLock('payment-account', route.params.id as string)

const paymentAccount = ref<PaymentAccountDetails>(new PaymentAccountDetails())

const paymentAccountTypeSelectOptions = ref<
    { label: string; value: string | null }[]
>([])

async function loadPaymentAccountTypeSelectOptions(search: string) {
    const paymentAccountRequest = await getPaymentAccounts({
        'filter[type]': search,
    })
    paymentAccountTypeSelectOptions.value =
        paymentAccountRequest.data.payment_accounts.map(
            (paymentAccount: PaymentAccount) => {
                return {
                    label: paymentAccount.type as string,
                    value: paymentAccount.type,
                }
            }
        )
}

function goToDetails() {
    if (paymentAccount.value.id) {
        router.push({
            name: 'payment_account',
            params: { id: paymentAccount.value.id },
        })
    }
}

function toPaymentAccounts() {
    router.push({
        name: 'payment-accounts',
    })
}

async function savePaymentAccount() {
    const payload = { ...paymentAccount.value }
    if (!payload.enable_stakeholder_payment) {
        payload.stakeholder_fee = null
    }
    if (paymentAccount.value.id) {
        try {
            await updatePaymentAccount(payload)

            toast.success(
                i18n.t('toast_success_payment_account_saved', {
                    paymentAccount: paymentAccount.value.id,
                })
            )
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            console.error(error)
            toast.error(
                i18n.t('toast_error_payment_account_not_saved', {
                    paymentAccount: paymentAccount.value.id,
                    error: error.response.data.message,
                })
            )
        }
    } else {
        const savedPaymentAccount = await createPaymentAccount(payload)
        paymentAccount.value.id = savedPaymentAccount.data.id
        toast.success(
            i18n.t('toast_success_payment_account_saved', {
                paymentAccount: paymentAccount.value.id,
            })
        )
    }
}

async function loadPaymentAccount() {
    if (route.params.id) {
        const paymentAccountRequest = await getPaymentAccount(
            route.params.id as string
        )
        paymentAccount.value = paymentAccountRequest.data
    }
}

onMounted(async () => {
    await loadPaymentAccount()
})
</script>

<style scoped></style>
